import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BcEmployeeAuthFacade } from '@bayclubs/employee-auth';
import { AuthService, MenuItem } from '@bayclubs/horizon';
import { TranslateService } from '@ngx-translate/core';
import { FiltersStorageService } from '@shared/shared.module';
import { filter, map } from 'rxjs/operators';
import { SecuritySignalService } from 'src/app/infrastructure/services/security-signal.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class MenubarFacade {
    private readonly isMsal = environment.isMsalAuthenticationEnabled;
    private readonly auth = inject(BcEmployeeAuthFacade);
    private readonly msalAuth = this.isMsal ? inject(AuthService) : null;
    private readonly securitySignals = inject(SecuritySignalService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly filtersStorageService = inject(FiltersStorageService);

    private readonly canUseRecordedClass = computed(() =>
        this.securitySignals.canViewPlaylists() || this.securitySignals.canViewRecordedClass()
    );

    private readonly urlSignal = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.router.url)
        ),
        { initialValue: this.router.url }
    );

    readonly items = computed<MenuItem[]>(() => {
        // Track URL to re-evaluate remembered URLs as filters change.
        this.urlSignal();

        const items: MenuItem[] = [];

        if (this.securitySignals.canViewClasses()) {
            items.push(
                this.buildRememberedItem(
                    'Calendar',
                    '/management/classes/calendar',
                    () => this.filtersStorageService.calendarUrl,
                    (url) => url === '/management/classes/calendar'
                ),
                this.buildRememberedItem(
                    'List',
                    '/management/classes',
                    () => this.filtersStorageService.listUrl,
                    (url) => url.startsWith('/management/classes') && !url.startsWith('/management/classes/calendar')
                )
            );
        }

        if (this.securitySignals.canManageProviders()) {
            items.push({
                label: this.translateService.instant('NavBarMenu.ProvidersListMenuItemLabel'),
                routerLink: '/trainers/list',
                active: this.currentPath().startsWith('/trainers')
            });
        }

        if (this.securitySignals.canViewSubstitutionRequests()) {
            items.push(
                this.buildRememberedItem(
                    this.translateService.instant('NavBarMenu.SubstitutionBoardMenuItemLabel'),
                    '/subboard',
                    () => this.filtersStorageService.subBoardUrl
                )
            );
        }

        if (this.securitySignals.canViewReports()) {
            items.push({
                label: this.translateService.instant('NavBarMenu.ReportsMenuItemLabel'),
                routerLink: '/reports'
            });
        }

        if (this.canUseRecordedClass()) {
            const subItems: MenuItem[] = [];
            if (this.securitySignals.canViewRecordedClass()) {
                subItems.push({
                    label: this.translateService.instant('NavBarMenu.RecordedClasses'),
                    routerLink: '/recordings'
                });
            }
            if (this.securitySignals.canViewPlaylists()) {
                subItems.push({
                    label: this.translateService.instant('NavBarMenu.Playlists'),
                    routerLink: '/playlists'
                });
            }
            items.push({
                label: this.translateService.instant('NavBarMenu.RecordedContent'),
                items: subItems
            });
        }

        if (this.securitySignals.canViewClubs()) {
            items.push({
                label: this.translateService.instant('NavBarMenu.Configuration'),
                routerLink: '/configuration'
            });
        }

        return items;
    });

    // MSAL: BcEmployeeAuthFacade.token$ stays empty (no token exchange) - read the MSAL account instead.
    readonly userName = this.isMsal
        ? computed(() => this.firstName(this.msalAuth!.userName()))
        : toSignal(
            this.auth.token$.pipe(map(token => this.firstName(token?.name))),
            { initialValue: null }
        );

    logout(): void {
        if (this.isMsal) {
            this.msalAuth!.logoutRedirect();
        } else {
            this.auth.logOut();
        }
    }

    private firstName(name: string | null | undefined): string | null {
        if (!name) {
            return null;
        }
        const parts = name.trim().split(/\s+/).filter(Boolean);
        return parts.length > 0 ? parts[0] : null;
    }

    private buildRememberedItem(
        label: string,
        baseUrl: string,
        rememberedUrl: () => string | null | undefined,
        isActive?: (url: string) => boolean
    ): MenuItem {
        const remembered = rememberedUrl();
        const rememberedParams = this.extractQueryParams(remembered);
        return {
            label,
            routerLink: baseUrl,
            queryParams: rememberedParams
                ? (rememberedParams as unknown as Record<string, string | number | boolean | null | undefined>)
                : undefined,
            active: isActive ? isActive(this.currentPath()) : undefined
        };
    }

    private currentPath(): string {
        const url = this.router.url || '';
        const queryParamsIndex = url.indexOf('?');
        return queryParamsIndex === -1 ? url : url.slice(0, queryParamsIndex);
    }


    private extractQueryParams(url: string | null | undefined): Record<string, string | string[]> | null {
        if (!url) {
            return null;
        }
        const queryIndex = url.indexOf('?');
        if (queryIndex === -1) {
            return null;
        }
        const query = url.slice(queryIndex + 1);
        const params = new URLSearchParams(query);
        const result: Record<string, string | string[]> = {};
        params.forEach((_value, key) => {
            const values = params.getAll(key);
            result[key] = values.length > 1 ? values : values[0];
        });
        return Object.keys(result).length ? result : null;
    }
}
